import React, { useState } from "react";

import { quadrantToothData } from "../../../utils/helpers/commonHelper";
import { paths } from "../../../utils/helpers/dentalHelper";
import { PALLETTE } from "../../../utils/theme";
import { V2ReportStyle } from "../../../styles/V2ReportStyle";
import GlobalStyle from "../../../styles/GlobalStyle";

// Types
type ToothStatus = "D" | "M" | "F";
type Quadrant = "Q1" | "Q2" | "Q3" | "Q4";

// -- COORDINATES AND DATA (Keeping your exact logic) --
const VIEWBOX_WIDTH = 265;
const PATH_HEIGHT = 175;
const VERTICAL_SPACE = 50;
const CHART_VIEWBOX_HEIGHT = PATH_HEIGHT * 2 + VERTICAL_SPACE;
const NEW_BOTTOM_Y_START = PATH_HEIGHT + VERTICAL_SPACE;

const STATUS_COLORS = {
  D: { fill: PALLETTE.redShade1, stroke: PALLETTE.red, text: "#333333" },
  M: { fill: PALLETTE.yellowShade1, stroke: PALLETTE.yellow, text: "#333333" },
  F: { fill: PALLETTE.blueShade1, stroke: PALLETTE.blue, text: "#FFFFFF" },
};

const FullDentalChart = ({ statusData }: { statusData: any }) => {
  const [activeQuadrant, setActiveQuadrant] = useState<Quadrant | false>(false);

  const getToothStatus = (toothId: string) => {
    const status = statusData?.[toothId];
    return (
      STATUS_COLORS[status] || {
        fill: "transparent",
        stroke: "transparent",
        text: "#A0A6B8",
      }
    );
  };

  const renderIndicator = (tooth: any) => {
    const { fill, stroke, text } = getToothStatus(tooth.id);
    const isLower = tooth.isLower;
    const finalY = isLower
      ? NEW_BOTTOM_Y_START + (PATH_HEIGHT - tooth.y)
      : tooth.y;
    const finalTextY = isLower ? finalY - 3.5 : tooth.y + 3.5;

    return (
      <g key={`${tooth.id}-indicator`}>
        {statusData?.[tooth.id] ? (
          <circle cx={tooth.x} cy={finalY} r={5} fill={fill} stroke={stroke} />
        ) : (
          <text
            x={tooth.x}
            y={finalTextY}
            fontSize={"1.2rem"}
            fill="#A0A6B8"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {tooth.label}
          </text>
        )}
      </g>
    );
  };

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <svg
        width="100%"
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${CHART_VIEWBOX_HEIGHT}`}
        style={{ maxWidth: "600px", cursor: "pointer" }}
      >
        {["Q1", "Q2", "Q3", "Q4"].map((q) => (
          // <g key={q} onClick={() => setActiveQuadrant(q)}>
          <g key={q}>
            {quadrantToothData[q].map((tooth: any) => (
              <React.Fragment key={tooth.id}>
                <path
                  d={paths[tooth.pathKey]}
                  fill="#A0A6B8"
                  opacity={0.34}
                  transform={
                    tooth.isLower
                      ? `scale(1, -1) translate(0, -${CHART_VIEWBOX_HEIGHT})`
                      : ""
                  }
                />
                {renderIndicator(tooth)}
              </React.Fragment>
            ))}
          </g>
        ))}
      </svg>

      {/* Modal / Overlay */}
      {activeQuadrant && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setActiveQuadrant(false)}
        >
          <div
            style={{
              ...V2ReportStyle.modalBox,
              backgroundColor: "white",
              padding: 20,
              borderRadius: 10,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <span style={{ ...GlobalStyle.blackBoldText, fontSize: "2.2rem" }}>
              QUADRANT {activeQuadrant.replace("Q", "")}
            </span>
            <svg
              viewBox={`0 0 ${VIEWBOX_WIDTH} ${CHART_VIEWBOX_HEIGHT}`}
              width={300}
            >
              {/* Re-render logic for specific quadrant in modal */}
            </svg>
            <button onClick={() => setActiveQuadrant(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FullDentalChart;
